import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ProductAPI } from "../../api/productApi";
import { BasketAPI } from "../../api/basketApi";
import { ShoppingCart, Truck } from "lucide-react";
import { Disclosure, DisclosureButton, DisclosurePanel, Button } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import NumericUpDown from "../numericUpDown/numericUpDown";
import { PartsFilter } from "../filter/productFilter";
import { useAuthEvent } from "../../hooks/useAuthEvent";

export enum ProviderType {
  None = 0,
  Shate = 1,
  Rossko = 2,
}

interface PriceItem {
  value: number;
  available: number;
  deliveryDate: string;
  providerType: ProviderType;
  providerOrderInfo?: string;
  temporaryPriceGuid?: string;
  partUniqueId?: string;
}

interface Product {
  brandName: string;
  code: string;
  content?: string;
  name: string;
  description?: string;
  prices: PriceItem[];
}

interface ProductVariant {
  brand: string;
  code: string;
  uniqueId: string;
  prices: PriceItem[];
  lowestPrice: number;
  closestDeliveryDate: string;
}

interface AddedToCartItem {
  added: boolean;
  response: {
    id: number;
  };
}

const ProductPage = () => {
  const [searchParams] = useSearchParams();
  const uniqueId = searchParams.get("part_id");
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const hasFetchedPrices = useRef(false);

  const [originalProducts, setOriginalProducts] = useState<ProductVariant[]>([]);
  const [analogProducts, setAnalogProducts] = useState<ProductVariant[]>([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [pricesLoaded, setPricesLoaded] = useState(false);

  const [addedToCart, setAddedToCart] = useState<Record<string, AddedToCartItem>>({});

  // Добавляем состояние для фильтров
  const [filteredOriginalProducts, setFilteredOriginalProducts] = useState<ProductVariant[]>([]);
  const [filteredAnalogProducts, setFilteredAnalogProducts] = useState<ProductVariant[]>([]);

  // Подписываемся на события авторизации
  useAuthEvent((user) => {
    console.log('User authenticated, refreshing product prices...');
    // Сбрасываем флаг загрузки цен и перезагружаем их
    hasFetchedPrices.current = false;
    setPricesLoaded(false);
    setOriginalProducts([]);
    setAnalogProducts([]);
    setFilteredOriginalProducts([]);
    setFilteredAnalogProducts([]);
    // Перезагружаем цены
    fetchPrices();
  });

  // Функция для фильтрации продуктов
  const handleFilterChange = (filters: {
    brands: string[];
    minPrice?: number;
    maxPrice?: number;
    deliveryDate?: string;
  }) => {
    const filterProducts = (products: ProductVariant[]) => {
      return products.filter(product => {
        // Фильтр по бренду
        if (filters.brands.length > 0 && !filters.brands.includes(product.brand)) {
          return false;
        }

        // Фильтр по цене
        if (filters.minPrice && product.lowestPrice < filters.minPrice) {
          return false;
        }
        if (filters.maxPrice && product.lowestPrice > filters.maxPrice) {
          return false;
        }

        // Фильтр по дате доставки
        if (filters.deliveryDate) {
          const deliveryDate = new Date(product.closestDeliveryDate);
          const today = new Date();
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);

          if (filters.deliveryDate === 'today' && 
              deliveryDate.toDateString() !== today.toDateString()) {
            return false;
          }
          if (filters.deliveryDate === 'tomorrow' && 
              deliveryDate.toDateString() !== tomorrow.toDateString()) {
            return false;
          }
        }

        return true;
      });
    };

    setFilteredOriginalProducts(filterProducts(originalProducts));
    setFilteredAnalogProducts(filterProducts(analogProducts));
  };

  // Получаем уникальные бренды из продуктов
  const getUniqueBrands = () => {
    const brands = new Set<string>();
    [...originalProducts, ...analogProducts].forEach(product => {
      brands.add(product.brand);
    });
    return Array.from(brands);
  };

  useEffect(() => {
    const fetchProduct = async () => {
      if (!uniqueId) return;
      setLoading(true);
      try {
        const data = await ProductAPI.getProductById(uniqueId);
        setProduct(data);
        fetchPrices();
      } catch (error) {
        console.error("Ошибка загрузки товара:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [uniqueId]);

  const fetchPrices = async () => {
    if (!uniqueId || pricesLoaded || loadingPrices || hasFetchedPrices.current) return;
    hasFetchedPrices.current = true;

    setLoadingPrices(true);
    try {
      const safeUniqueId = uniqueId as string;
      const data = await ProductAPI.getProductPrices(safeUniqueId);
      const original = data.original.map((item: ProductVariant) => ({
        ...item,
        prices: item.prices.map((price: PriceItem) => ({
          ...price,
          partUniqueId: safeUniqueId
        }))
      }));
      const analogs = data.analogs.map((item: ProductVariant) => ({
        ...item,
        prices: item.prices.map((price: PriceItem) => ({
          ...price,
          partUniqueId: item.uniqueId
        }))
      }));
      
      setOriginalProducts(original);
      setAnalogProducts(analogs);
      setFilteredOriginalProducts(original);
      setFilteredAnalogProducts(analogs);
      setPricesLoaded(true);
    } catch (error) {
      console.error("Ошибка загрузки цен:", error);
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleAddToCart = async (item: PriceItem, uid: string) => {
    try {
      const response = await BasketAPI.addItem(uid, {
        value: item.value,
        available: item.available,
        deliveryDate: item.deliveryDate,
        providerType: item.providerType,
        providerOrderInfo: item.providerOrderInfo,
        temporaryPriceGuid: item.temporaryPriceGuid,
      });
  
      setAddedToCart((prev) => ({
        ...prev,
        [item.temporaryPriceGuid || '']: {
          added: true,
          response: response,
        },
      }));
    } catch (error) {
      console.error("Ошибка добавления товара в корзину:", error);
    }
  };

  const handleChangeBasketItemQuantity = async (basketItemId: number, count: number) => {
    try {
      await BasketAPI.updateItem(basketItemId, count);
    } catch (error) {
      console.error("Ошибка изменения товара в корзине:", error);
    }
  };

  const renderPricesTable = (prices: PriceItem[], uid: string) => {
    return (
      <table className="min-w-full">
        <tbody>
          {prices.map((item, index) => (
            <tr key={index}>
              <td className="py-4">
                <div className="flex items-center gap-2">
                  <Truck className="size-4 shrink-0 text-muted-foreground" />
                  <span>{new Date(item.deliveryDate).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                </div>
              </td>
              <td className="py-4">{item.available} шт.</td>
              <td className="py-4">{item.value} ₽</td>
              <td className="py-4 float-right">
                {addedToCart[item.temporaryPriceGuid || '']?.added ? (
                  <NumericUpDown
                    value={1}
                    max={item.available}
                    onChange={(value: number) => handleChangeBasketItemQuantity(addedToCart[item.temporaryPriceGuid || ''].response.id, value)}
                  />
                ) : (
                  <Button
                    className="flex items-center gap-2 rounded bg-gray-100 py-2 px-2 text-sm text-gray-500 data-[hover]:bg-gray-200"
                    onClick={() => handleAddToCart(item, item.partUniqueId || uid)}
                  >
                    <ShoppingCart className="size-4 shrink-0 text-muted-foreground" />
                    <span>В корзину</span>
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderProductBlock = (
    product: ProductVariant,
    index: number,
    minPrice: number,
    minDate: string
  ) => {
    return (
      <Disclosure key={index} as="div" className="mb-2">
        {({ open }) => (
          <>
            <DisclosureButton className="flex w-full justify-between 
              pt-1 pb-1 pl-2 pr-2items-start rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex-1 text-left">
                <p className="text-lg font-semibold">{product.brand}</p>
                <p className="text-lg text-gray-500/75 font-light">
                  <a href={`/product?part_id=${product.uniqueId}`} className="hover:underline">
                    {product.code}
                  </a>
                </p>
              </div>
              <div className="flex-1 text-left">
                <p className="text-lg font-normal">От <b>{minPrice}р</b></p>
                <p className="text-lg font-light flex gap-3 items-center">
                  {(() => {
                    const date = new Date(minDate);
                    const time = date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", hour12: false });
                    const shortDate = date.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
                    const isToday = date.toDateString() === new Date().toDateString();

                    return (
                      <>
                        <Truck className={`size-6 shrink-0 ${isToday ? "text-green-600/50" : "text-gray-500/75"}`} />
                        <span className="font-light">
                          {isToday ? "Сегодня" : `${shortDate}, ${time}`}
                        </span>
                      </>
                    );
                  })()}
                </p>
              </div>
              <ChevronDownIcon className={`w-5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </DisclosureButton>
            <DisclosurePanel className="pt-2 transition-all duration-200 ease-out pl-2 pr-2">
              {product.prices.length > 0 ? (
                renderPricesTable(product.prices, product.uniqueId)
              ) : (
                <p className="text-gray-500 font-light">Нет данных о ценовых предложениях</p>
              )}
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    );
  };

  return (
    <>
      <div className="flex w-0 flex-1">
        {/* Статичные фильтры слева */}
        <div className="w-64 flex-shrink-0 pr-6 max-md:hidden">
          <div className="sticky top-6 pt-4">
            <PartsFilter
              onFilterChange={handleFilterChange}
              brands={getUniqueBrands()}
            />
          </div>
        </div>
        
        {/* Основной контент */}
        <div className="flex-1 flex flex-col p-4">
        {/* Статическая область - информация о товаре */}
        <div className="py-10">
          {loading ? (
            <p className="text-center">...</p>
          ) : product ? (
            <div className="w-full">
              <div className="mb-6">
                <h1 className="text-1xl font-bold" style={{ display: 'inline', verticalAlign: 'top'}}>
                  <span>{product.brandName}</span>
                  <span className="pl-4 font-medium text-gray-400/50">{product.code}</span>
                </h1>
                {product.content ? (
                  <img src={product.content} alt={product.name} className="ml-3 mb-6"
                  width="300" style={{ display: 'inline', float: 'right', border: '1px solid rgba(250, 250, 250, 0.61)'}}/>
                ) : (
                  <></>
                )}
              </div>
              <p className="pt-0 font-normal">{product.name}</p>
              <p className="mb-6 text-gray-500">{product.description}</p>
            </div>
          ) : (
            <p className="text-center text-red-500">Товар не найден</p>
          )}
        </div>

        {/* Область с ценовыми предложениями */}
        <div className="pr-4">
          {loadingPrices ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="loader mb-4"></div>
              <p className="text-gray-500">Загружаем предложения...</p>
            </div>
          ) : (
            <>
              <div className="md:hidden mb-6">
                <PartsFilter
                    onFilterChange={handleFilterChange}
                    brands={getUniqueBrands()}
                  />
              </div>
              
              {/* Оригинальные запчасти - отображаем сразу */}
              <div className="w-full mb-8">
                <div className="flex w-full justify-between items-center border-b-2 pb-2 border-black/50 mb-4">
                  <span className="text-lg font-semibold">Оригинальные запчасти</span>
                </div>
                <div className="pt-2">
                  {filteredOriginalProducts.length > 0 ? (
                    filteredOriginalProducts.map((product, index) => renderProductBlock(product, index, product.lowestPrice, product.closestDeliveryDate))
                  ) : (
                    <p className="text-gray-500 font-light">Нет данных о ценовых предложениях</p>
                  )}
                </div>
              </div>

              {/* Аналоги - отображаем сразу */}
              {pricesLoaded && (
                <div className="w-full pt-5">
                  <div className="flex w-full justify-between items-center border-b-2 pb-2 border-black/50 mb-4">
                    <span className="text-lg font-semibold">Аналоги</span>
                  </div>
                  <div className="pt-2">
                    {filteredAnalogProducts.length > 0 ? (
                      filteredAnalogProducts.map((product, index) => renderProductBlock(product, index, product.lowestPrice, product.closestDeliveryDate))
                    ) : (
                      <p className="text-gray-500 font-light">Нет данных о ценовых предложениях</p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </div>
      </div>
    </>
  );
};

export default ProductPage;